import { getCurrentUTCDateTime } from 'utils/time'
import {
  CONSENT_SECTION_TYPES,
  USER_TYPES,
  RESPONSE_STATES,
  SUBMISSION_STATES,
  QUESTION_TYPES
} from 'utils/constants'

/** Types shown as comprehension UI before/after signature (matches ConsentComprehension). */
export const CONSENT_COMPREHENSION_QUESTION_TYPES = [
  QUESTION_TYPES.selectOne,
  QUESTION_TYPES.selectMultiple,
  QUESTION_TYPES.text
]

const resolveQuestionFromId = (questions, id) => {
  if (!questions || !id) return null
  if (questions[id]) return questions[id]
  // Some order entries include a suffix (e.g. logic branch) like "l56efeb_c427f20".
  // Fall back to the base question id if present.
  if (typeof id === 'string' && id.includes('_')) {
    const baseId = id.split('_')[0]
    if (questions[baseId]) return questions[baseId]
  }
  return null
}

/**
 * Comprehension sections that appear before the first renderable signature in form order.
 */
export const getPreSignatureComprehensionSections = (sections, hasCaregiver) => {
  if (!Array.isArray(sections)) return []
  const firstSigIdx = sections.findIndex(section => {
    if (section?.type !== CONSENT_SECTION_TYPES.signature) return false
    const userType = section?.attributes?.user_type
    return userType === USER_TYPES.participant || (userType === USER_TYPES.caregiver && hasCaregiver)
  })
  if (firstSigIdx <= 0) return []
  return sections
    .slice(0, firstSigIdx)
    .filter(s => s && CONSENT_COMPREHENSION_QUESTION_TYPES.includes(s.type))
}

/**
 * Pre-sign flow sections in payload order, up to (but excluding) first renderable signature.
 * Includes intro/prompt plus comprehension question types.
 */
export const getPreSignatureFlowSections = (sections, hasCaregiver) => {
  if (!Array.isArray(sections)) return []
  const firstSigIdx = sections.findIndex(section => {
    if (section?.type !== CONSENT_SECTION_TYPES.signature) return false
    const userType = section?.attributes?.user_type
    return userType === USER_TYPES.participant || (userType === USER_TYPES.caregiver && hasCaregiver)
  })
  const slice = firstSigIdx === -1 ? sections : sections.slice(0, firstSigIdx)
  return slice.filter(
    s =>
      s &&
      (s.type === CONSENT_SECTION_TYPES.prompt ||
        s.type === CONSENT_SECTION_TYPES.pdf ||
        s.type === QUESTION_TYPES.introduction ||
        CONSENT_COMPREHENSION_QUESTION_TYPES.includes(s.type))
  )
}

/**
 * Payload shape for consent API (mirrors ConsentComprehension.completeQuestion).
 */
export const formatConsentComprehensionAnswerForApi = submission => {
  const { id, answer, type } = submission
  const toArray = Array.isArray(answer) ? answer : answer ? [answer] : []
  if (type === QUESTION_TYPES.text) {
    return toArray
  }
  return toArray.map(ans => {
    const rawValue = ans?.value
    const choiceId =
      typeof rawValue === 'string' && rawValue.includes('_')
        ? rawValue.split('_').pop()
        : rawValue
    return { ...ans, value: `${id}_${choiceId}`, question: id }
  })
}

const minChoicesMet = (section, count) => {
  const { min_choices: minChoices, required } = section.attributes || {}
  const min = minChoices != null && minChoices !== '' ? Number(minChoices) : null
  if (min != null && !Number.isNaN(min)) return count >= min
  if (required === false) return true
  return count >= 1
}

const maxChoicesMet = (section, count) => {
  const { max_choices: maxChoices } = section.attributes || {}
  const max = maxChoices != null && maxChoices !== '' ? Number(maxChoices) : null
  if (max == null || Number.isNaN(max)) return true
  return count <= max
}

/**
 * Whether required pre-signature comprehension questions have valid answers in submission.
 */
export const arePreSignatureComprehensionQuestionsSatisfied = (sections, submissionQuestions) => {
  if (!Array.isArray(sections) || sections.length === 0) return true
  const subs = submissionQuestions || []
  return sections.every(s => {
    if (s.attributes?.required === false) return true
    const q = subs.find(x => x.id === s.id)
    const answers = Array.isArray(q?.answer) ? q.answer : []
    const count = answers.length

    if (s.type === QUESTION_TYPES.text) {
      if (count < 1) return false
      const v = answers[0]?.value
      return !!(v && String(v).trim())
    }
    if (s.type === QUESTION_TYPES.selectOne) {
      return count >= 1 && answers.some(a => a?.value != null && a.value !== '')
    }
    if (s.type === QUESTION_TYPES.selectMultiple) {
      return minChoicesMet(s, count) && maxChoicesMet(s, count)
    }
    return count >= 1
  })
}

const formatQuestionForSubmission = (id, question) => {
  const { attributes, label, type } = question
  return {
    id,
    label,
    name: id,
    type,
    required: attributes.required,
    user_type: attributes.user_type,
    state: RESPONSE_STATES.answered,
    answer: []
  }
}

export const formatSubmissionConsent = (instrument) => {
  const formattedContent = {
    questions: [],
    state: SUBMISSION_STATES.valid,
    start: getCurrentUTCDateTime(),
    completion: '',
    version: instrument?.consent_version,
    consent_id: instrument?.consent_id
  }
  if (instrument.order) {
    instrument.order[0].forEach((id) => {
      const question = instrument.questions[id]
      const formattedQuestion = formatQuestionForSubmission(id, question)
      formattedContent.questions.push(formattedQuestion)
    })
  }
  return formattedContent
}

export const checkAndAddCompletedConsentQuestion = (questions, id, data) => {
  const questionIdx = questions.findIndex((q) => q.id === id)
  if (questionIdx === -1) return questions

  const type = questions[questionIdx]?.type
  const newAnswer = {
    // For signatures, handle both single values and arrays of signature objects.
    // For all comprehension question types, expect an array of {label, value, question} objects.
    answer:
      type === CONSENT_SECTION_TYPES.signature
        ? Array.isArray(data)
          ? data.map((sig) => {
            const entry = {
              label: questions[questionIdx].label,
              question: id,
              value: sig.value,
              signature_type: sig.signature_type
            }
            if (sig.file_name) entry.file_name = sig.file_name
            if (sig.mime_type) entry.mime_type = sig.mime_type
            return entry
          })
          : [
            {
              label: questions[questionIdx].label,
              question: id,
              value: data,
              signature_type: 'draw'
            }
          ]
        : Array.isArray(data)
          ? data
          : []
  }

  // Return a new array with a new question object so Redux detects the update (enables I Agree button).
  return questions.map((q, idx) =>
    idx === questionIdx ? { ...q, ...newAnswer } : q
  )
}

export const allSignaturesComplete = (submission, extraChecks = {}) => {
  // Check if extraChecks are provided (preferred method)
  const {
    participantNameEntered,
    participantSignatureEntered,
    participantSkipped,
    caregiverNameEntered,
    caregiverSignatureEntered,
    caregiverSkipped
  } = extraChecks

  // If extraChecks are provided, use those for validation
  if (
    typeof participantSignatureEntered === 'boolean' ||
    typeof participantNameEntered === 'boolean' ||
    typeof caregiverSignatureEntered === 'boolean' ||
    typeof caregiverNameEntered === 'boolean'
  ) {
    // Check participant validation if provided
    if (typeof participantSignatureEntered === 'boolean') {
      // If participant was skipped, allow completion even if name/signature are empty
      if (participantSkipped) return true
      // Signature is always required when checking participant
      if (!participantSignatureEntered) return false
    }

    // Check participant name if name is required
    if (typeof participantNameEntered === 'boolean') {
      // Name is required when explicitly passed
      if (!participantNameEntered) return false
    }

    // Check caregiver validation if provided
    if (typeof caregiverSignatureEntered === 'boolean') {
      // If caregiver was skipped, allow completion even if name/signature are empty
      if (caregiverSkipped) return true
      // Signature is always required when checking caregiver
      if (!caregiverSignatureEntered) return false
    }

    // Check caregiver name if name is required
    if (typeof caregiverNameEntered === 'boolean') {
      // Name is required when explicitly passed
      if (!caregiverNameEntered) return false
    }

    return true
  }

  // Fallback to submission checks if no extraChecks provided
  if (!submission?.questions) return false

  const signatureAnswers = submission.questions.filter(
    (q) =>
      q.type === CONSENT_SECTION_TYPES.signature &&
      q.user_type === USER_TYPES.participant
  )
  if (signatureAnswers.length === 0) return false
  const participantComplete = signatureAnswers.every((sig) => {
    // If question is marked optional and was explicitly skipped, treat as complete
    if (sig?.skipped && sig?.required === false) return true
    return (
      Array.isArray(sig.answer) &&
      sig.answer.length > 0 &&
      !!sig.answer[0].value
    )
  })
  if (!participantComplete) return false

  // Check caregiver signatures in submission data
  const caregiverSignatureAnswers = submission.questions.filter(
    (q) =>
      q.type === CONSENT_SECTION_TYPES.signature &&
      q.user_type === USER_TYPES.caregiver
  )

  // If there are caregiver signatures in submission data, check if they are complete or skipped
  if (caregiverSignatureAnswers.length > 0) {
    const caregiverComplete = caregiverSignatureAnswers.every((sig) => {
      // If question is marked optional and was explicitly skipped, treat as complete
      if (sig?.skipped && sig?.required === false) return true
      return (
        Array.isArray(sig.answer) &&
        sig.answer.length > 0 &&
        !!sig.answer[0].value
      )
    })
    if (!caregiverComplete) return false
  }

  return true
}

export const getSectionsArr = (questions, order) => {
  if (!questions) return []
  const output = []
  const ids =
    Array.isArray(order?.[0]) && order[0].length
      ? order[0]
      : Object.keys(questions)
  ids.forEach((id) => {
    const q = resolveQuestionFromId(questions, id)
    if (!q) return
    output.push({ id, ...q })
  })
  return output
}

export const addCompletionDate = (submission) => {
  const newData = {
    completion: getCurrentUTCDateTime(),
    consent_id: submission?.consent_id,
    version: submission?.version
  }
  return Object.assign({}, submission, newData)
}

const isNewerCompletedConsent = (candidate, existing) => {
  const candidateVersion = candidate?.last_deployed_version ?? 0
  const existingVersion = existing?.last_deployed_version ?? 0
  if (candidateVersion !== existingVersion) {
    return candidateVersion > existingVersion
  }
  const candidateCompleted = candidate?.completed
    ? new Date(candidate.completed).getTime()
    : 0
  const existingCompleted = existing?.completed
    ? new Date(existing.completed).getTime()
    : 0
  return candidateCompleted > existingCompleted
}

/**
 * API returns one row per signed deployment; redeployed consents repeat consent_id.
 * Keep only the latest signed version per consent_id for display.
 */
export const dedupeCompletedConsentsByLatestVersion = (consents) => {
  if (!Array.isArray(consents) || consents.length <= 1) {
    return consents || []
  }

  const latestById = new Map()
  const order = []

  consents.forEach(consent => {
    const id = consent?.consent_id
    if (!id) return

    const existing = latestById.get(id)
    if (!existing) {
      order.push(id)
      latestById.set(id, consent)
      return
    }
    if (isNewerCompletedConsent(consent, existing)) {
      latestById.set(id, consent)
    }
  })

  return order.map(id => latestById.get(id))
}

export const normalizeQuestionStatesForSubmission = (submission) => {
  if (!submission?.questions) return submission
  const questions = submission.questions.map((q) => {
    const isEmptyAnswer =
      !Array.isArray(q.answer) || q.answer.length === 0 || !q.answer[0]?.value
    if (isEmptyAnswer) {
      return Object.assign({}, q, { state: RESPONSE_STATES.skipped })
    }
    return Object.assign({}, q, { state: RESPONSE_STATES.answered })
  })
  return Object.assign({}, submission, { questions })
}

export const NON_BLOCKING_CONSENT_CLOSED = 'nonBlockingConsentClosed'

export const setConsentClosedToLocalStorage = () => {
  localStorage.setItem(NON_BLOCKING_CONSENT_CLOSED, 'true')
}

export const getConsentClosedFromLocalStorage = () => {
  const value = localStorage.getItem(NON_BLOCKING_CONSENT_CLOSED)

  return !!value
}

export const removeConsentClosedFromLocalStorage = () => {
  if (getConsentClosedFromLocalStorage()) {
    localStorage.removeItem(NON_BLOCKING_CONSENT_CLOSED)
  }
}

export const isBlockConsent = (user, consent) => {
  if (!user.study) return false
  // Caregivers should not be blocked by consent dialogues
  if (user?.is_caregiver) return false
  const isBlockingConsent = consent?.consentForm?.is_blocking
  const hasPendingConsents = (consent?.v2Consents?.length ?? 0) > 0

  // GOLD-11140: When there are pending consents and the loaded form is blocking, show modal even if
  // user.has_consented is true (they may have completed a previous consent; the new one is not signed yet).
  if (hasPendingConsents && isBlockingConsent && user.study.consent_required) {
    return true
  }
  // User already completed consent and no pending blocking consent; do not show blocking modal
  if (user?.has_consented) return false

  const isConsentClosed = getConsentClosedFromLocalStorage()

  if (!isBlockingConsent && isConsentClosed) {
    return false
  }

  return user.study.consent_required ? !!isBlockingConsent : false
}